/**
 * Application Workflow Step Configuration and Mapping Utilities
 * 
 * This module defines the mapping between backend application stages and frontend
 * stepper navigation steps. It provides utilities for step navigation, accessibility
 * control, and section rendering.
 */

/**
 * Workflow step definitions with order and associated backend stages
 * Each step represents a phase in the application workflow
 */
export const WORKFLOW_STEPS = [
  { 
    id: 'shortlisted', 
    label: 'Shortlisted', 
    order: 1, 
    stages: ['SHORTLISTED'] 
  },
  { 
    id: 'agreement', 
    label: 'Agreement', 
    order: 2, 
    stages: ['AGREEMENT'] 
  },
  { 
    id: 'interview', 
    label: 'Interview', 
    order: 3, 
    stages: ['INTERVIEW'] 
  },
  { 
    id: 'offer', 
    label: 'Offer', 
    order: 4, 
    stages: ['OFFER_LETTER'] 
  },
  { 
    id: 'payment', 
    label: 'Payment', 
    order: 5, 
    stages: ['PAYMENT'] 
  },
  { 
    id: 'documents', 
    label: 'Documents', 
    order: 6, 
    stages: ['DOCUMENT_VERIFICATION'] 
  },
  { 
    id: 'exam', 
    label: 'Exam', 
    order: 7, 
    stages: ['EXAM'] 
  },
  { 
    id: 'medical', 
    label: 'Medical', 
    order: 8, 
    stages: ['MEDICAL'] 
  },
  { 
    id: 'tpc', 
    label: 'TPC Practising', 
    order: 9, 
    stages: ['TPC_PRACTISING'] 
  },
  { 
    id: 'esd', 
    label: 'ESD Approval', 
    order: 10, 
    stages: ['ESD_APPROVAL'] 
  },
  { 
    id: 'visa', 
    label: 'Visa', 
    order: 11, 
    stages: ['VISA_PROCESS'] 
  },
  { 
    id: 'predeparture', 
    label: 'Pre-Departure', 
    order: 12, 
    stages: ['PRE_DEPARTURE'] 
  },
  { 
    id: 'clearance', 
    label: 'Clearance', 
    order: 13, 
    stages: ['CLEARANCE'] 
  },
  { 
    id: 'ticket', 
    label: 'Ticket', 
    order: 14, 
    stages: ['TICKET'] 
  },
  { 
    id: 'departure', 
    label: 'Departure', 
    order: 15, 
    stages: ['DEPARTURE'] 
  },
  { 
    id: 'completed', 
    label: 'Completed', 
    order: 16, 
    stages: ['COMPLETED', 'REJECTED', 'WITHDRAWN'] 
  }
];

/**
 * Mapping from backend application.currentStage to frontend step identifier
 * This allows conversion from backend stage values to stepper step IDs
 */
export const STAGE_TO_STEP_MAP = {
  'APPLIED': 'shortlisted',
  'SHORTLISTED': 'shortlisted',
  'AGREEMENT': 'agreement',
  'INTERVIEW': 'interview',
  'OFFER_LETTER': 'offer',
  'PAYMENT': 'payment',
  'DOCUMENT_VERIFICATION': 'documents',
  'EXAM': 'exam',
  'MEDICAL': 'medical',
  'TPC_PRACTISING': 'tpc',
  'ESD_APPROVAL': 'esd',
  'VISA_PROCESS': 'visa',
  'PRE_DEPARTURE': 'predeparture',
  'CLEARANCE': 'clearance',
  'TICKET': 'ticket',
  'DEPARTURE': 'departure',
  'COMPLETED': 'completed',
  'REJECTED': 'completed',
  'WITHDRAWN': 'completed'
};

/**
 * Mapping from step ID to the component/section that should be rendered
 * null indicates no specific workflow section (show candidate profile only)
 */
export const STEP_TO_SECTION_MAP = {
  'shortlisted': null,
  'agreement': 'AgreementWorkflowSection',
  'interview': 'InterviewSection',
  'offer': 'OfferLetterSection',
  'payment': 'OfferLetterSection',
  'documents': 'DocumentUpload',
  'exam': 'InterviewSection',
  'medical': 'DocumentUpload',
  'tpc': 'TPCESDSection',
  'esd': 'TPCESDSection',
  'visa': 'DocumentUpload',
  'predeparture': 'InterviewSection',
  'clearance': 'ClearanceSection',
  'ticket': 'TicketSection',
  'departure': 'DepartureSection',
  'completed': null
};

/**
 * Convert a backend application stage to a frontend step identifier
 * 
 * @param {string} stage - The backend application.currentStage value
 * @returns {string} The corresponding step ID, defaults to 'applied' if not found
 */
export const getStepFromStage = (stage) => {
  if (!stage) {
    return 'applied';
  }
  
  const stepId = STAGE_TO_STEP_MAP[stage];
  
  if (!stepId) {
    console.warn(`Unknown stage: ${stage}, defaulting to 'applied'`);
    return 'applied';
  }
  
  return stepId;
};

/**
 * Get the order number for a given step ID
 * 
 * @param {string} stepId - The step identifier
 * @returns {number} The order number, or 0 if step not found
 */
export const getStepOrder = (stepId) => {
  const step = WORKFLOW_STEPS.find(s => s.id === stepId);
  return step ? step.order : 0;
};

/**
 * Determine if a target step is accessible based on the current application stage
 * Users can access the current step and any previous steps, but not future steps
 * 
 * @param {string} targetStepId - The step the user wants to navigate to
 * @param {string} currentStage - The current backend application.currentStage
 * @returns {boolean} True if the step is accessible, false otherwise
 */
export const isStepAccessible = (targetStepId, currentStage) => {
  if (!targetStepId || !currentStage) {
    return false;
  }
  
  const currentStepId = getStepFromStage(currentStage);
  const targetOrder = getStepOrder(targetStepId);
  const currentOrder = getStepOrder(currentStepId);
  
  // Allow access to current step and all previous steps
  return targetOrder <= currentOrder && targetOrder > 0;
};
